import React from 'react';
import { formatIST } from '@/lib/utils';
import { CheckCircle2, Clock, User, Wrench, MessageSquare, GitBranch } from 'lucide-react';

export default function BatchHistoryPanel({ batch }) {
  const hasHistory = batch.stage_history?.length > 0;

  return (
    <div className="border-t">
      {/* Split Information */}
      {batch.split_from && (
        <div className="bg-purple-50/50 px-3 py-2 border-b border-purple-100">
          <div className="flex items-center gap-2 mb-1">
            <GitBranch className="h-3.5 w-3.5 text-purple-500" />
            <span className="text-[10px] font-semibold text-purple-700 uppercase tracking-wider">Split From Batch #{batch.split_from.parent_batch_number}</span>
          </div>
          <div className="grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-2 text-[11px] text-slate-600">
            <div className="flex items-center gap-1">
              <Clock className="h-3 w-3 text-slate-400" />
              <span>{batch.split_from.split_at ? formatIST(batch.split_from.split_at) : '-'}</span>
            </div>
            <div className="flex items-center gap-1">
              <User className="h-3 w-3 text-slate-400" />
              <span>{batch.split_from.split_by_name || 'Unknown'}</span>
            </div>
            <div>
              <span className="text-slate-400">At stage:</span> <span className="font-medium">{batch.split_from.split_at_stage}</span>
            </div>
            <div>
              <span className="text-green-600 font-medium">{batch.split_from.passed_qty?.toLocaleString()}</span>
              <span className="text-slate-400"> passed, </span>
              <span className="text-purple-600 font-medium">{batch.split_from.remaining_qty?.toLocaleString()}</span>
              <span className="text-slate-400"> remained</span>
            </div>
          </div>
          {batch.split_from.remarks && (
            <div className="flex items-start gap-1 mt-2 text-[11px] text-slate-500 bg-white/50 rounded px-2 py-1">
              <MessageSquare className="h-3 w-3 mt-0.5 flex-shrink-0 text-purple-400" />
              <span className="italic">"{batch.split_from.remarks}"</span>
            </div>
          )}
        </div>
      )}

      {/* Has Children indicator */}
      {(batch.has_splits || batch.child_batch_ids?.length > 0) && (
        <div className="bg-orange-50/50 px-3 py-2 border-b border-orange-100">
          <div className="flex items-center gap-2 text-[11px] text-orange-700">
            <GitBranch className="h-3.5 w-3.5 text-orange-500" />
            <span>This batch was split into {batch.child_batch_ids?.length || 0} child batch(es)</span>
          </div>
        </div>
      )}

      {/* Stage History */}
      {hasHistory && (
        <div className="bg-slate-50/50 px-3 py-2 space-y-2">
          <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Stage Completion History</p>
          {batch.stage_history.map((hist, hi) => (
            <div key={hi} className="relative pl-5 pb-2 last:pb-0">
              {/* Timeline connector */}
              <div className="absolute left-1.5 top-1 w-0.5 h-full bg-slate-200" style={hi === batch.stage_history.length - 1 ? { height: '8px' } : {}} />
              <div className="absolute left-0 top-1 w-3.5 h-3.5 rounded-full bg-green-500 border-2 border-white flex items-center justify-center">
                <CheckCircle2 className="h-2.5 w-2.5 text-white" />
              </div>

              <div className="bg-white rounded-md border border-slate-150 p-2.5 shadow-sm">
                {/* Stage name + timestamp */}
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs font-semibold text-slate-700">{hist.stage_name}</span>
                  <span className="text-[10px] text-slate-400">
                    {hist.completed_at ? formatIST(hist.completed_at) : '\u2014'}
                  </span>
                </div>

                {/* Operator & Machine */}
                <div className="flex items-center gap-3 text-[11px] text-slate-500 mb-1">
                  {hist.operator && (
                    <span className="flex items-center gap-1">
                      <User className="h-3 w-3 text-slate-400" /> {hist.operator}
                    </span>
                  )}
                  {hist.machine_used && (
                    <span className="flex items-center gap-1">
                      <Wrench className="h-3 w-3 text-slate-400" /> {hist.machine_used}
                    </span>
                  )}
                </div>

                {/* Quantities */}
                <div className="flex items-center gap-3 text-[11px]">
                  <span className="text-green-600 font-medium">{hist.qty_completed?.toLocaleString()} completed</span>
                  {hist.qty_rejected > 0 && (
                    <span className="text-red-500 font-medium">{hist.qty_rejected?.toLocaleString()} rejected</span>
                  )}
                </div>

                {/* Remarks - Highlighted */}
                {hist.remarks && hist.remarks.trim() && (
                  <div className="mt-2 flex items-start gap-1.5 bg-orange-50 border border-orange-200 rounded-md px-2.5 py-1.5">
                    <MessageSquare className="h-3.5 w-3.5 text-orange-500 mt-0.5 flex-shrink-0" />
                    <p className="text-[11px] text-orange-800 leading-relaxed">{hist.remarks}</p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
